/**
 * Pure Access Control Tag (ACT) core.
 *
 * Framework-agnostic membership logic shared by the sageweb admin/cpanel
 * `useAct` hooks and `ActGate` components. Deliberately dependency-free so it
 * stays trivially testable and reusable across apps.
 */

export const COMPANY_ADMIN_ROLE = "COMPANY_ADMIN";

/**
 * `NEXT_PUBLIC_ACT_ENFORCEMENT=off` reverts every frontend to legacy
 * role-based behaviour (a global kill switch for the rollout).
 */
export const ACT_ENFORCEMENT_ENV = "NEXT_PUBLIC_ACT_ENFORCEMENT";

export interface ActSubject {
  /**
   * Effective active ACT slugs from `/users/me/`. `null`/`undefined` marks a
   * legacy user whose payload predates ACTs — treated as "allow everything".
   */
  acts?: string[] | null;
  roleNames: string[];
  isSageStaff?: boolean;
}

/**
 * Tiers that sit above the ACT model entirely: platform staff and company
 * admins are backfilled with every ACT, so they bypass membership checks.
 */
export function isActBypass(subject: ActSubject): boolean {
  return Boolean(
    subject.isSageStaff || subject.roleNames?.includes(COMPANY_ADMIN_ROLE),
  );
}

function isEnforcementOff(): boolean {
  return (
    typeof process !== "undefined" &&
    process.env?.[ACT_ENFORCEMENT_ENV] === "off"
  );
}

/**
 * Resolve whether `subject` holds `slug`.
 *
 * Precedence: bypass tier → legacy fallback (`acts == null`) → kill switch →
 * membership. Additive-only: nothing an ACT grants is ever subtracted here.
 */
export function checkAct(slug: string, subject: ActSubject): boolean {
  if (isActBypass(subject)) return true;
  if (subject.acts == null) return true;
  if (isEnforcementOff()) return true;
  return subject.acts.includes(slug);
}
